/**
 * Reset Password Page
 * User enters the Twilio Verify SMS code + chooses a new password
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button, Input, Card, toast } from '../components/ui';
import apiClient from '../lib/api';

export const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [phone, setPhone] = useState(searchParams.get('phone') ?? '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);
    try {
      await apiClient.post('/auth/reset-password', {
        phone,
        userType: 'BUSINESS',
        code,
        password,
      });
      toast.success('Password reset — you can sign in now');
      navigate('/login');
    } catch (err: any) {
      toast.error(
        err.response?.data?.error?.message ||
          err.response?.data?.message ||
          'Invalid or expired code'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-orange-400 mb-4">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h1 className="text-h1 text-black font-bold">Buffables</h1>
          <p className="text-base text-black-lighter mt-2">Business Dashboard</p>
        </div>

        <Card>
          <div className="p-8">
            <h2 className="text-h3 text-black font-semibold mb-2">Set a new password</h2>
            <p className="text-base text-black-lighter mb-6">
              Enter the verification code we texted you and pick a new password.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <Input
                type="tel"
                id="phone"
                label="Phone number"
                placeholder="(555) 123-4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                isRequired
                autoComplete="tel"
              />

              <Input
                type="text"
                id="code"
                label="Verification code"
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                isRequired
                autoComplete="one-time-code"
                inputMode="numeric"
              />

              <Input
                type="password"
                id="password"
                label="New password"
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                isRequired
                autoComplete="new-password"
              />

              <Input
                type="password"
                id="confirmPassword"
                label="Confirm new password"
                placeholder="Re-enter password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                isRequired
                autoComplete="new-password"
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isLoading}
                className="w-full"
              >
                Reset password
              </Button>
            </form>

            <div className="mt-6 flex items-center justify-center gap-4 text-base">
              <Link
                to="/forgot-password"
                className="text-orange-400 hover:text-orange-300 font-semibold transition-colors"
              >
                Resend code
              </Link>
              <span className="text-black-lighter">·</span>
              <Link
                to="/login"
                className="text-orange-400 hover:text-orange-300 font-semibold transition-colors"
              >
                Back to sign in
              </Link>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
